import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Download, Trash2, Search, Barcode, Wand2, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";

export default function UniqueIdsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, available: 0, registered: 0 });
  const [status, setStatus] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [agents, setAgents] = useState([]);
  const [agentSummary, setAgentSummary] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [defaultSku, setDefaultSku] = useState("");
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [selectedAssignOpen, setSelectedAssignOpen] = useState(false);
  const [selectedAssignSku, setSelectedAssignSku] = useState("");
  const [selectedAssignTitle, setSelectedAssignTitle] = useState("");

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (q) p.set("q", q);
    if (agentFilter) p.set("agent_id", agentFilter);
    const r = await api.get(`/unique_ids?${p.toString()}`);
    setItems(r.data.items || []);
    setCounts(r.data.counts || {});
    setAgentSummary(r.data.agent_summary || null);
  }, [status, q, agentFilter]);
  useEffect(() => {
    load();
    api.get("/agents").then((r) => setAgents(r.data)).catch(() => setAgents([]));
  }, [load]);

  const bulkUpload = async (e) => {
    e.preventDefault();
    if (!text.trim() && !file) {
      return toast.error("Paste some rows or select a CSV file");
    }
    setBusy(true);
    try {
      let payloadText = text;
      if (file) {
        payloadText = (payloadText ? payloadText + "\n" : "") + await file.text();
      }
      const r = await api.post("/unique_ids/bulk", {
        text: payloadText,
        default_sku: defaultSku,
      });
      toast.success(`${r.data.added} added · ${r.data.duplicate} duplicates skipped`);
      setOpen(false);
      setText("");
      setDefaultSku("");
      setFile(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (code) => {
    if (!window.confirm(`Delete unique ID ${code}?`)) return;
    try {
      await api.delete(`/unique_ids/${encodeURIComponent(code)}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const exportUrl = `${process.env.REACT_APP_BACKEND_URL}/api/unique_ids/export.csv`;

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSku, setAssignSku] = useState("");
  const [assignTitle, setAssignTitle] = useState("");
  const [assignScope, setAssignScope] = useState("empty"); // "empty" | "all_available"

  const bulkAssign = async (e) => {
    e.preventDefault();
    if (!assignSku.trim()) return toast.error("Enter the SKU to assign");
    setBusy(true);
    try {
      const payload = { sku: assignSku.trim(), product_title: assignTitle.trim() };
      if (assignScope === "empty") payload.filter_sku_empty = true;
      else payload.filter_status = "available";
      const r = await api.post("/unique_ids/bulk_assign_sku", payload);
      toast.success(`Updated ${r.data.updated} codes → ${assignSku.trim()}`);
      setAssignOpen(false);
      setAssignSku("");
      setAssignTitle("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleRow = (code) => setSelected((prev) => {
    const s = new Set(prev);
    if (s.has(code)) s.delete(code); else s.add(code);
    return s;
  });
  const toggleAll = () => setSelected((prev) => {
    if (prev.size === items.length) return new Set();
    return new Set(items.map((i) => i.code));
  });
  const clearSelection = () => setSelected(new Set());

  const assignSelected = async (e) => {
    e.preventDefault();
    if (!selectedAssignSku.trim()) return toast.error("Enter the SKU");
    setBusy(true);
    try {
      const r = await api.post("/unique_ids/bulk_assign_sku", {
        codes: Array.from(selected),
        sku: selectedAssignSku.trim(),
        product_title: selectedAssignTitle.trim(),
      });
      toast.success(`Updated ${r.data.updated} selected code${r.data.updated === 1 ? "" : "s"}`);
      setSelectedAssignOpen(false);
      setSelectedAssignSku("");
      setSelectedAssignTitle("");
      clearSelection();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteSelected = async () => {
    const arr = Array.from(selected);
    if (!arr.length) return;
    if (!window.confirm(`Delete ${arr.length} unique ID${arr.length === 1 ? "" : "s"}? Registered codes will be skipped by the server.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/unique_ids/bulk_delete", { codes: arr });
      toast.success(`Deleted ${r.data.deleted} code${r.data.deleted === 1 ? "" : "s"}`);
      clearSelection();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="data-label mb-1">Product serial master</div>
          <h1 className="font-heading font-black text-4xl tracking-tight">Unique Product IDs</h1>
          <p className="text-sm text-[#71717A] mt-1 max-w-2xl">
            Upload the unique per-unit serials shipped to stores. When a Sales Agent registers a product,
            the system will match its Unique Product ID against this master list before the Sales Incentives team can approve the incentive.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => window.open(exportUrl, "_blank")} data-testid="export-unique-ids-csv">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
          {isAdmin && (
            <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="bulk-assign-sku-button">
                  <Wand2 className="w-4 h-4 mr-1" /> Bulk assign SKU
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Bulk assign SKU to uploaded codes</DialogTitle>
                  <DialogDescription>
                    Retro-tag your already-uploaded Unique Product IDs with a product SKU so the incentive engine can find the right amount. Make sure the SKU has an entry on the <span className="font-mono">Incentives</span> page first.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={bulkAssign} className="space-y-4">
                  <div>
                    <Label className="data-label mb-1.5 block">Product SKU</Label>
                    <Input
                      required
                      value={assignSku}
                      onChange={(e) => setAssignSku(e.target.value)}
                      placeholder="e.g. WINN 101"
                      data-testid="bulk-assign-sku-input"
                    />
                  </div>
                  <div>
                    <Label className="data-label mb-1.5 block">Product title (optional)</Label>
                    <Input
                      value={assignTitle}
                      onChange={(e) => setAssignTitle(e.target.value)}
                      placeholder="Displayed name (defaults to SKU)"
                      data-testid="bulk-assign-title-input"
                    />
                  </div>
                  <div>
                    <Label className="data-label mb-1.5 block">Apply to</Label>
                    <div className="space-y-2 text-sm">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="scope" checked={assignScope === "empty"} onChange={() => setAssignScope("empty")} data-testid="bulk-scope-empty" />
                        <span>Only codes with empty SKU (recommended)</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="scope" checked={assignScope === "all_available"} onChange={() => setAssignScope("all_available")} data-testid="bulk-scope-all" />
                        <span>All available (not yet registered) codes — overrides existing SKU</span>
                      </label>
                    </div>
                  </div>
                  <Button type="submit" disabled={busy} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white w-full" data-testid="bulk-assign-submit">
                    {busy ? "Assigning…" : "Assign SKU"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
          {isAdmin && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#4F46E5] hover:bg-[#4338CA] text-white" data-testid="upload-unique-ids-button">
                  <Upload className="w-4 h-4 mr-1" /> Upload / Paste IDs
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Bulk upload Unique Product IDs</DialogTitle>
                  <DialogDescription>
                    Paste one code per line, or provide <span className="font-mono">code,sku,title</span>. Blank lines and lines starting with # are ignored. You can also attach a CSV.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={bulkUpload} className="space-y-4">
                  <div>
                    <Label className="data-label mb-1.5 block">Default SKU (used when a row has only a code)</Label>
                    <Input
                      value={defaultSku}
                      onChange={(e) => setDefaultSku(e.target.value)}
                      placeholder="e.g. HEM-WC-101"
                      data-testid="unique-default-sku"
                    />
                  </div>
                  <div>
                    <Label className="data-label mb-1.5 block">Paste rows</Label>
                    <Textarea
                      rows={8}
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder={"HEM-U001,HEM-WC-101,Standard Wheelchair\nHEM-U002\nHEM-U003,HEM-BED-01,Hospital Bed"}
                      data-testid="unique-text-input"
                      className="font-mono text-xs"
                    />
                  </div>
                  <div>
                    <Label className="data-label mb-1.5 block">Or upload CSV</Label>
                    <input
                      type="file"
                      accept=".csv,text/csv,text/plain"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                      data-testid="unique-csv-file"
                      className="text-sm"
                    />
                  </div>
                  <Button type="submit" disabled={busy} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white w-full" data-testid="unique-submit">
                    {busy ? "Uploading…" : "Upload"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-shell p-5" data-testid="ku-total">
          <div className="data-label">Total codes uploaded</div>
          <div className="mt-2 font-heading font-black text-3xl">{counts.total}</div>
        </div>
        <div className="card-shell p-5" data-testid="ku-available">
          <div className="data-label">Available (unused)</div>
          <div className="mt-2 font-heading font-black text-3xl text-[#059669]">{counts.available}</div>
        </div>
        <div className="card-shell p-5" data-testid="ku-registered">
          <div className="data-label">Registered</div>
          <div className="mt-2 font-heading font-black text-3xl text-[#4F46E5]">{counts.registered}</div>
        </div>
      </div>

      <div className="card-shell p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <Input
            className="pl-9"
            placeholder="Search by code, SKU, or title…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="unique-search"
          />
        </div>
        <Select value={status || "__all"} onValueChange={(v) => setStatus(v === "__all" ? "" : v)}>
          <SelectTrigger className="w-[180px]" data-testid="unique-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All</SelectItem>
            <SelectItem value="available">Available</SelectItem>
            <SelectItem value="registered">Registered</SelectItem>
          </SelectContent>
        </Select>
        <Select value={agentFilter || "__all"} onValueChange={(v) => setAgentFilter(v === "__all" ? "" : v)}>
          <SelectTrigger className="w-[220px]" data-testid="unique-agent-filter"><SelectValue placeholder="All agents" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All agents</SelectItem>
            {agents.map((a) => (
              <SelectItem key={a.id} value={a.id} data-testid={`unique-agent-option-${a.id}`}>
                {a.name}{a.salesperson_id ? ` · ${a.salesperson_id}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} data-testid="unique-apply">Apply</Button>
      </div>

      {agentSummary && (
        <div className="card-shell p-4 bg-[#EEF2FF] border-[#C7D2FE] flex flex-wrap items-center gap-6" data-testid="agent-summary-strip">
          <div>
            <div className="data-label">Sales agent</div>
            <div className="font-heading font-bold text-lg">{agentSummary.agent_name}</div>
          </div>
          <div>
            <div className="data-label">Salesperson ID</div>
            <div className="font-mono text-sm">{agentSummary.salesperson_id || "—"}</div>
          </div>
          <div>
            <div className="data-label">Codes registered</div>
            <div className="font-heading font-black text-xl">{agentSummary.registered_count}</div>
          </div>
          <div>
            <div className="data-label">Total incentive earned</div>
            <div className="font-heading font-black text-xl text-[#059669]">₹{Number(agentSummary.total_incentive).toLocaleString("en-IN")}</div>
          </div>
        </div>
      )}

      {isAdmin && selected.size > 0 && (
        <div className="card-shell p-3 flex flex-wrap items-center justify-between gap-3 bg-[#EEF2FF] border-[#C7D2FE]" data-testid="unique-bulk-bar">
          <div className="text-sm">
            <span className="font-medium">{selected.size}</span> code{selected.size === 1 ? "" : "s"} selected
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={clearSelection} data-testid="unique-clear-selection">
              <X className="w-3 h-3 mr-1" /> Clear
            </Button>
            <Dialog open={selectedAssignOpen} onOpenChange={setSelectedAssignOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="unique-bulk-assign-selected">
                  <Wand2 className="w-3 h-3 mr-1" /> Assign SKU to selected
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Assign SKU to {selected.size} selected code{selected.size === 1 ? "" : "s"}</DialogTitle>
                  <DialogDescription>Overrides any existing SKU on the selected rows.</DialogDescription>
                </DialogHeader>
                <form onSubmit={assignSelected} className="space-y-4">
                  <div>
                    <Label className="data-label mb-1.5 block">Product SKU</Label>
                    <Input required value={selectedAssignSku} onChange={(e) => setSelectedAssignSku(e.target.value)} placeholder="e.g. WINN 101" data-testid="unique-bulk-selected-sku" />
                  </div>
                  <div>
                    <Label className="data-label mb-1.5 block">Product title (optional)</Label>
                    <Input value={selectedAssignTitle} onChange={(e) => setSelectedAssignTitle(e.target.value)} data-testid="unique-bulk-selected-title" />
                  </div>
                  <Button type="submit" disabled={busy} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white w-full" data-testid="unique-bulk-selected-submit">
                    {busy ? "Assigning…" : `Assign to ${selected.size}`}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            <Button size="sm" variant="outline" className="text-[#DC2626] hover:bg-[#FEE2E2]" onClick={deleteSelected} disabled={busy} data-testid="unique-bulk-delete-selected">
              <Trash2 className="w-3 h-3 mr-1" /> Delete selected
            </Button>
          </div>
        </div>
      )}

      <div className="card-shell overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                {isAdmin && (
                  <th style={{ width: 40 }}>
                    <Checkbox
                      checked={items.length > 0 && selected.size === items.length}
                      onCheckedChange={toggleAll}
                      data-testid="unique-select-all"
                    />
                  </th>
                )}
                <th>Code</th><th>SKU</th><th>Title</th><th>Status</th>
                <th>Ticket</th><th>Sales Agent</th><th className="text-right">Incentive</th>
                <th>Uploaded</th><th>Registered on</th><th></th>
              </tr>
            </thead>
            <tbody data-testid="unique-ids-tbody">
              {items.length === 0 ? (
                <tr><td colSpan={isAdmin ? 11 : 10} className="p-10 text-center text-[#71717A] text-sm">
                  <Barcode className="w-8 h-8 mx-auto mb-2 text-[#A1A1AA]" />
                  No unique IDs {q || status ? "match your filters" : "uploaded yet"}.
                </td></tr>
              ) : items.map((it) => (
                <tr key={it.code} data-testid={`unique-row-${it.code}`}>
                  {isAdmin && (
                    <td>
                      <Checkbox
                        checked={selected.has(it.code)}
                        onCheckedChange={() => toggleRow(it.code)}
                        data-testid={`unique-check-${it.code}`}
                      />
                    </td>
                  )}
                  <td className="font-mono">{it.code}</td>
                  <td className="font-mono text-xs">{it.sku || "—"}</td>
                  <td className="text-sm">{it.product_title || "—"}</td>
                  <td>
                    <span
                      className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-mono"
                      style={{
                        backgroundColor: it.status === "registered" ? "#EEF2FF" : "#DCFCE7",
                        color: it.status === "registered" ? "#4338CA" : "#166534",
                      }}
                    >
                      {it.status}
                    </span>
                  </td>
                  <td className="font-mono text-xs">
                    {it.ticket_id ? (
                      <a href={`/records/${it.record_id}`} className="text-[#4F46E5] hover:underline" data-testid={`unique-ticket-${it.code}`}>{it.ticket_id}</a>
                    ) : "—"}
                  </td>
                  <td className="text-sm" data-testid={`unique-agent-${it.code}`}>
                    {it.agent_name ? (
                      <>
                        <div>{it.agent_name}</div>
                        <div className="font-mono text-[10px] text-[#71717A]">{it.agent_salesperson_id || ""}</div>
                      </>
                    ) : "—"}
                  </td>
                  <td className="text-right font-mono font-medium" data-testid={`unique-incentive-${it.code}`}>
                    {it.incentive_amount != null ? `₹${Number(it.incentive_amount).toLocaleString("en-IN")}` : "—"}
                    {it.incentive_approval_status && (
                      <div className={`text-[10px] uppercase tracking-widest font-mono ${
                        it.incentive_approval_status === "approved" ? "text-[#166534]" :
                        it.incentive_approval_status === "rejected" ? "text-[#991B1B]" : "text-[#92400E]"
                      }`}>{it.incentive_approval_status}</div>
                    )}
                  </td>
                  <td className="text-xs text-[#71717A]">{(it.uploaded_at || "").slice(0, 10)}</td>
                  <td className="text-xs text-[#71717A]">{(it.registered_at || "").slice(0, 10) || "—"}</td>
                  <td>
                    {isAdmin && it.status !== "registered" && (
                      <Button size="sm" variant="ghost" onClick={() => del(it.code)} data-testid={`delete-unique-${it.code}`}>
                        <Trash2 className="w-4 h-4 text-[#DC2626]" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
